(() => {
  if (customElements.get('vk-cover-set')) return;
  class VinkovaCoverSet extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.info = this.closest('product-info') || this.closest('.shopify-section');
      this.main = this.querySelector('[data-vk-set-main]');
      this.items = this.querySelector('[data-vk-set-items]');
      this.badge = this.querySelector('[data-vk-set-badge]');
      this.summary = this.querySelector('[data-vk-set-summary]');
      this.error = this.querySelector('[data-vk-set-error]');
      this.catalog = JSON.parse(this.querySelector('[data-vk-set-catalog]').textContent);
      this.selected = new Set();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.items.addEventListener('click', event => {
        const item = event.target.closest('[data-vk-set-item]');
        if (!item) return;
        const id = Number(item.dataset.id);
        if (this.selected.has(id)) this.selected.delete(id);
        else this.selected.add(id);
        this.showError('');
        this.update();
      }, options);
      this.querySelector('[data-vk-set-all]').addEventListener('click', () => {
        this.others().forEach(cover => this.selected.add(cover.id));
        this.showError('');
        this.update();
      }, options);
      this.info?.addEventListener('change', () => queueMicrotask(() => this.update()), options);
      this.info?.addEventListener('input', () => this.update(), options);
      this.form.addEventListener('variant:change', () => this.update(), options);
      this.form.addEventListener('submit', event => {
        if (!this.selection().length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submit(event.submitter);
      }, { ...options, capture: true });
      this.update();
      this.refresh().catch(() => {});
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.restorePayments();
      this.ready = false;
    }

    money(cents) {
      const format = window.theme?.settings?.moneyFormat;
      if (format && window.theme?.Currency?.formatMoney) return window.theme.Currency.formatMoney(cents, format);
      return new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency || 'EUR', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
    }

    quantity() { return Math.max(1, Number(new FormData(this.form).get('quantity') || 1)); }
    currentId() { return Number(this.form.querySelector('[name="id"]')?.value); }
    covers() { return this.catalog.covers.filter(cover => cover.productId === Number(this.dataset.productId)); }
    current() { return this.covers().find(cover => cover.id === this.currentId()); }
    discounted() { return !this.catalog.cart.matchingBlocked; }

    // The other cover types of this print that can still be added.
    others() {
      const current = this.current();
      if (!current) return [];
      return this.covers().filter(cover => cover.id !== current.id && cover.type !== current.type && cover.stock > cover.inCart);
    }

    selection() { return this.others().filter(cover => this.selected.has(cover.id)); }

    // Same rule as the automatic discount «10% fundas de aparato»: it only applies with three covers,
    // and then the two cheapest get 10 % off (checked against the real cart on 19-oct-2026).
    setOffer() { return this.discounted() && Boolean(this.current()) && this.others().length === 2; }
    cheapest(covers) { return [...covers].sort((a, b) => a.price - b.price).slice(0, 2); }
    total(covers) {
      const sum = covers.reduce((total, cover) => total + cover.price, 0);
      if (!this.discounted() || covers.length < 3) return sum;
      return sum - this.cheapest(covers).reduce((total, cover) => total + Math.round(cover.price * 0.1), 0);
    }

    image(cover) {
      if (cover.crop?.some((value, index) => value !== [0, 0, 100, 100][index])) {
        const image = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        const [x, y, width, height] = cover.crop.map((value, index) => value * (index % 2 ? cover.imageHeight : cover.imageWidth) / 100);
        image.setAttribute('viewBox', [x, y, width, height].join(' '));
        image.setAttribute('aria-hidden', 'true');
        const frame = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        Object.entries({ x, y, width, height, overflow: 'hidden' }).forEach(([name, value]) => frame.setAttribute(name, value));
        const source = document.createElementNS('http://www.w3.org/2000/svg', 'image');
        source.setAttribute('href', cover.image);
        source.setAttribute('x', -x);
        source.setAttribute('y', -y);
        source.setAttribute('width', cover.imageWidth);
        source.setAttribute('height', cover.imageHeight);
        frame.append(source);
        image.append(frame);
        return image;
      }
      const image = document.createElement('img');
      image.src = cover.image;
      image.alt = '';
      image.width = 120;
      image.height = 100;
      image.loading = 'lazy';
      return image;
    }

    card(cover, fullSet) {
      const chosen = this.selected.has(cover.id);
      const inSet = fullSet && this.cheapest(fullSet).some(item => item.id === cover.id);
      const price = inSet ? cover.price - Math.round(cover.price * 0.1) : cover.price;
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'vk-set__item';
      item.dataset.vkSetItem = '';
      item.dataset.id = cover.id;
      item.setAttribute('aria-pressed', String(chosen));
      const photo = document.createElement('span');
      photo.className = 'vk-set__photo';
      photo.append(this.image(cover));
      const name = document.createElement('span');
      name.className = 'vk-set__name';
      name.textContent = cover.variantTitle;
      const prices = document.createElement('span');
      prices.className = 'vk-set__price';
      if (price < cover.price) {
        const full = document.createElement('s');
        full.textContent = this.money(cover.price);
        prices.append(full);
      }
      const final = document.createElement('strong');
      final.textContent = this.money(price);
      prices.append(final);
      if (inSet) {
        const caption = document.createElement('span');
        caption.className = 'vk-set__caption';
        caption.textContent = this.dataset.withSet;
        prices.append(caption);
      }
      const action = document.createElement('span');
      action.className = 'vk-set__add';
      action.textContent = chosen ? `✓ ${this.dataset.added}` : `+ ${this.dataset.add}`;
      item.append(photo, name, prices, action);
      return item;
    }

    update() {
      if (!this.catalog) return;
      const others = this.others();
      for (const id of this.selected) if (!others.some(cover => cover.id === id)) this.selected.delete(id);
      this.main.hidden = !others.length;
      this.hidden = !others.length && !this.dataset.hasMatches;
      const current = this.current();
      const chosen = others.filter(cover => this.selected.has(cover.id));
      const base = current ? [current, ...chosen] : chosen;
      const offer = this.setOffer();
      const fullSet = offer ? [current, ...others] : null;
      this.badge.hidden = !offer;
      const focused = document.activeElement?.closest?.('[data-vk-set-item]')?.dataset.id;
      this.items.replaceChildren(...others.map(cover => this.card(cover, fullSet)));
      if (focused && this.contains(document.activeElement) === false) this.items.querySelector(`[data-id="${focused}"]`)?.focus();
      const quantity = this.quantity();
      const shown = offer && !chosen.length ? fullSet : base;
      const full = shown.reduce((total, cover) => total + cover.price, 0) * quantity;
      const final = this.total(shown) * quantity;
      const missing = offer ? others.length - chosen.length : 0;
      this.summary.hidden = !chosen.length && !offer;
      const complete = offer && missing === 0;
      const label = !chosen.length ? this.dataset.fullLabel : complete ? this.dataset.totalLabel : this.dataset.selectionLabel;
      this.querySelector('[data-vk-set-label]').textContent = `${label} (${shown.length})`;
      const fullNode = this.querySelector('[data-vk-set-full]');
      fullNode.textContent = this.money(full);
      fullNode.hidden = final >= full;
      this.querySelector('[data-vk-set-total]').textContent = this.money(final);
      const saving = this.querySelector('[data-vk-set-saving]');
      const setSaving = offer ? (fullSet.reduce((total, cover) => total + cover.price, 0) - this.total(fullSet)) * quantity : 0;
      saving.textContent = complete && final < full ? this.dataset.saving.replace('{x}', this.money(full - final)) : offer && chosen.length && missing === 1 ? this.dataset.nudge.replace('{x}', this.money(setSaving)) : '';
      saving.hidden = !saving.textContent;
      this.querySelector('[data-vk-set-all]').hidden = !offer || missing === 0;
      this.querySelector('[data-vk-set-note]').hidden = !chosen.length;
      if (chosen.length && !this.paymentButtons) {
        this.paymentButtons = Array.from(this.form.querySelectorAll('.shopify-payment-button')).map(element => ({ element, hidden: element.hidden }));
        this.paymentButtons.forEach(({ element }) => { element.hidden = true; });
      } else if (!chosen.length) this.restorePayments();
    }

    restorePayments() {
      this.paymentButtons?.forEach(({ element, hidden }) => { element.hidden = hidden; });
      this.paymentButtons = null;
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async refresh() {
      if (this.refreshRequest) return this.refreshRequest;
      this.refreshRequest = (async () => {
        const url = new URL(this.dataset.catalogUrl, location.origin);
        url.searchParams.set('section_id', 'vinkova-equipment-cover-catalog');
        url.searchParams.set('_vk_refresh', String(Date.now()));
        const response = await fetch(url, { cache: 'no-store', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(this.dataset.loadError);
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const script = document.querySelector('[data-vk-equipment-catalog]');
        if (!script) throw new Error(this.dataset.loadError);
        const catalog = JSON.parse(script.textContent);
        if (!Array.isArray(catalog.covers) || typeof catalog.cart?.matchingBlocked !== 'boolean') throw new Error(this.dataset.loadError);
        this.catalog = catalog;
        this.update();
      })();
      try { await this.refreshRequest; } finally { this.refreshRequest = null; }
    }

    async submit(submitter) {
      if (this.submitting) return;
      this.submitting = true;
      this.showError('');
      const button = submitter || this.form.querySelector('[type="submit"]');
      const wasDisabled = button?.disabled;
      if (button) button.disabled = true;
      button?.setAttribute('aria-busy', 'true');
      let addedSuccessfully = false;
      try {
        const original = new FormData(this.form);
        const currentId = this.currentId();
        const quantity = this.quantity();
        const wanted = [...this.selected];
        await this.refresh();
        if (currentId !== this.currentId() || quantity !== this.quantity()) throw new Error(this.dataset.loadError);
        const chosen = this.selection();
        if (chosen.length !== wanted.length || chosen.some(cover => cover.stock < cover.inCart + quantity)) throw new Error(this.dataset.unavailable);
        const data = new FormData();
        for (const [name, value] of original) {
          if (name === 'id' || name === 'quantity' || name === 'selling_plan') data.append(`items[0][${name}]`, value);
          else if (/^properties\[.+\]$/.test(name)) data.append(`items[0][properties][${name.slice(11, -1)}]`, value);
          else data.append(name, value);
        }
        data.set('items[0][quantity]', String(quantity));
        chosen.forEach((cover, index) => {
          data.set(`items[${index + 1}][id]`, String(cover.id));
          data.set(`items[${index + 1}][quantity]`, String(quantity));
        });
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
        data.set('sections', [...new Set(sections)].slice(0, 5).join(','));
        data.set('sections_url', location.pathname);
        const root = this.dataset.root.replace(/\/$/, '');
        const response = await fetch(`${root}/cart/add.js`, { method: 'POST', body: data, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
        const added = await response.json();
        if (!response.ok || added.status) throw new Error(added.description || this.dataset.unavailable);
        addedSuccessfully = true;
        this.selected.clear();
        const cartResponse = await fetch(`${root}/cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error(this.dataset.loadError);
        const cart = await cartResponse.json();
        cart.sections = added.sections;
        window.theme?.pubsub?.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cart });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: added, vk: true } }));
        this.refresh().catch(() => {});
        const cesta = document.querySelector('vk-cesta');
        if (cesta?.show(added, added.sections?.[cesta.dataset.sectionId])) return;
        const drawer = document.querySelector('cart-drawer');
        if (drawer?.show) drawer.show(button);
        else location.href = `${root}/cart`;
      } catch (error) {
        if (addedSuccessfully) location.href = `${this.dataset.root.replace(/\/$/, '')}/cart`;
        else this.showError(error.message || this.dataset.loadError);
      } finally {
        this.submitting = false;
        if (button) button.disabled = wasDisabled;
        button?.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('vk-cover-set', VinkovaCoverSet);
})();
